import { ClassNames } from "@emotion/core";
import { StyledComponent } from "@emotion/styled";
import { ursaColors } from "@smartsheet/design-tokens";
import { ThemeKey } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { getClientId } from "@smartsheet/lodestar-utils";
import { LoggerContext, LogActionId } from "@smartsheet/logging-state-service";
import React, { DetailedHTMLProps, HTMLAttributes, Ref, useContext, useMemo, useState } from "react";
import { BadgeProps } from "../../shared/Badge";
import { useTheme } from "../../theme/utils/useTheme";
import { FOCUS_USING_BEFORE_CLASS_NAME } from "../../util/focusRing";
import { LoggableEvent, LoggedByDefault, LoggingDecorator, NotLoggedByDefault } from "../../util/logging";
import { FocusMode } from "../focusMode";
import { BaseButtonStyled } from "./styled/BaseButtonStyled";
import { ButtonBadge, getBadgeSize } from "./subComponents/ButtonBadge";
import { Content } from "./subComponents/Content";
import { IconWrapper } from "./subComponents/IconWrapper";
import { LoadingSpinner } from "./subComponents/LoadingSpinner";
import { ButtonAppearances, LinkAppearances, ButtonIconRenderProps, ButtonSizes, BaseButtonStyledProps } from "./types";

type BaseButtonNativeProps<RootElementType> = Omit<
    HTMLAttributes<RootElementType>,
    "onClick" | "onDoubleClick" | "onMouseUp" | "onMouseDown"
>;

export interface BaseButtonBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, "color"> {
    /**
     * Allow for any data- attributes passed to the badge
     */
    [key: `data-${string}`]: string | number;
    /**
     * By default the badge will be displayed with a strong emphasis (orange). Weak is forced
     * for disabled buttons, but is available for enabled buttons as well.
     */
    emphasis?: "strong" | "weak";

    /** Optional number to display in the badge (medium and large).
     *  If more than two digits are provided, the badge will display "99+".
     *  To display an empty badge, pass null.
     */
    number?: number | null;

    /**
     * A number (in px) to offset the location of the badge from it's default location.
     *
     * Note: This is relative to the default location of the badge
     */
    offset?: BadgeProps["offset"];
}

export interface BaseButtonProps<RootElementType extends HTMLButtonElement | HTMLAnchorElement>
    extends BaseButtonNativeProps<RootElementType> {
    focusMode: FocusMode;
    innerRef: Ref<RootElementType>;

    /**
     * @deprecated - the `appearance` prop is NOT going away, but the canvas button appearance options for this prop will be removed in lodestar-core V6+, coming February 2025
     * The base styling to apply to the button
     */
    appearance?: ButtonAppearances | LinkAppearances;
    /** Props for the optional badge */
    badgeProps?: BaseButtonBadgeProps;
    /** Children react nodes will be inserted as siblings to any generated icon nodes */
    children?: React.ReactNode;
    /**
     * A `clientId` prop is provided for specified elements, which is a unique string that appears as a data attribute
     * `data-client-id` in the rendered code, serving as a hook for automated tests
     */
    clientId?: string;

    /**
     * Normally padding is added to the middle content of the button (label area).  Set this to true
     *  to hide the center content area if the child content is empty.
     *  For example, an icon only button with a child tooltip.
     */
    hideEmptyContent?: boolean;

    /**
     * Places an icon within the button, after the button's text. If this is a function,
     * some of the Button's internal state will be provided for the icon to
     * style itself accordingly.
     */
    iconAfter?: React.ReactChild | ButtonIconRenderProps;
    /**
     * Places an icon within the button, before the button's text. If this is a function,
     * some of the Button's internal state will be provided for the icon to
     * style itself accordingly.
     */
    iconBefore?: React.ReactChild | ButtonIconRenderProps;
    /** Set if the button is disabled */
    isDisabled?: boolean;
    /**
     * Set if the button is loading. When isLoading is true, text is hidden, and
     * a spinner is shown in its place. The button maintains the width that it
     * would have if the text were visible.
     */
    isLoading?: boolean;
    /** Indicates this is a split button for icon margin handling */
    isSplit?: boolean;
    /** Handler to be called on click */
    onClick?: (e: React.MouseEvent<RootElementType>, log: LoggableEvent<LoggedByDefault>) => Promise<void> | void;
    /**
     * Handler to be called on a double-click.
     */
    onDoubleClick?: (e: React.MouseEvent<RootElementType>, log: LoggableEvent<LoggedByDefault>) => Promise<void> | void;
    onMouseDown?: (
        e: React.MouseEvent<RootElementType>,
        log: LoggableEvent<NotLoggedByDefault>
    ) => Promise<void> | void;
    onMouseUp?: (e: React.MouseEvent<RootElementType>, log: LoggableEvent<NotLoggedByDefault>) => Promise<void> | void;

    renderAs?: "button" | "a";

    /** Option to fit button width to its parent width */
    shouldFitContainer?: boolean;

    /**
     * Sets the size of the Button to one of the standard Lodestar sizes.
     */
    size?: ButtonSizes;

    /**
     * This is the base type for the Anchor and Button element attr 'type'.
     */
    type?: string;
}

const getBtnApperance = (
    appearance: ButtonAppearances | LinkAppearances,
    themeMode: ThemeKey
): { btnAppearance: ButtonAppearances | LinkAppearances; isCanvasEmptyAndLodestar: boolean } => {
    //https://app.smartsheet.com/sheets/J26gVQgGrGQ64wq5q9gRRMr2QcCGV5J8CMVhrgV1?rowId=4147589427138436 lodestar-light themed Buttons should map the canvas appeareances to secondary
    if (themeMode === "lodestar-light") {
        return {
            btnAppearance: appearance.includes("canvas") ? "secondary" : appearance,
            isCanvasEmptyAndLodestar: appearance === "canvas-empty" ? true : false,
        };
    }
    return { btnAppearance: appearance, isCanvasEmptyAndLodestar: false };
};

export const BaseButton = <RootElementType extends HTMLAnchorElement | HTMLButtonElement>({
    children,
    className,
    clientId,
    focusMode,
    iconAfter,
    iconBefore,
    innerRef,
    isDisabled = false,
    isLoading = false,
    renderAs = "button",
    shouldFitContainer = false,
    size = "large",
    tabIndex,
    badgeProps,
    appearance = renderAs === "button" ? "primary" : "link",
    ...rest
}: BaseButtonProps<RootElementType>) => {
    const logger = useContext(LoggerContext);
    const { key } = useTheme();
    const themeMode = key as ThemeKey;
    const { btnAppearance, isCanvasEmptyAndLodestar } = getBtnApperance(appearance, themeMode);
    const withLogging = new LoggingDecorator(logger, {
        clientId,
        controlType: "btn",
    });

    const [isActive, setIsActive] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const isInteractive = !isDisabled && !isLoading;

    const onMouseEnter = (e: React.MouseEvent<RootElementType>) => {
        setIsHovered(true);
        if (rest.onMouseEnter) {
            rest.onMouseEnter(e);
        }
    };

    const onMouseLeave = (e: React.MouseEvent<RootElementType>) => {
        setIsActive(false);
        setIsHovered(false);
        if (rest.onMouseLeave) {
            rest.onMouseLeave(e);
        }
    };

    const onMouseDown = (e: React.MouseEvent<RootElementType>) => {
        setIsActive(true);
        if (rest.onMouseDown) {
            void withLogging.disabledByDefault(
                {
                    actionId: LogActionId.MOUSE_DOWN,
                },
                rest.onMouseDown
            )(e);
        }
    };

    const onMouseUp = (e: React.MouseEvent<RootElementType>) => {
        setIsActive(false);
        if (rest.onMouseUp) {
            void withLogging.disabledByDefault(
                {
                    actionId: LogActionId.MOUSE_UP,
                },
                rest.onMouseUp
            )(e);
        }
    };

    const onClick = (e: React.MouseEvent<RootElementType>) => {
        if (isInteractive) {
            if (rest.onClick) {
                void withLogging.enabledByDefault(
                    {
                        actionId: LogActionId.CLICK,
                    },
                    rest.onClick
                )(e);
            }
        } else {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const onDoubleClick = (e: React.MouseEvent<RootElementType>) => {
        if (isInteractive) {
            if (rest.onDoubleClick) {
                void withLogging.enabledByDefault(
                    {
                        actionId: LogActionId.DOUBLE_CLICK,
                    },
                    rest.onDoubleClick
                )(e);
            }
        } else {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const onFocus: React.FocusEventHandler<RootElementType> = (event) => {
        setIsFocused(true);
        if (rest.onFocus) {
            rest.onFocus(event);
        }
    };

    const onBlur: React.FocusEventHandler<RootElementType> = (event) => {
        setIsFocused(false);
        if (rest.onBlur) {
            rest.onBlur(event);
        }
    };

    const hasLeftIcon = iconBefore !== undefined;
    const hasRightIcon = iconAfter !== undefined;

    const iconRenderState = {
        isActive,
        isFocus: isFocused,
        isHover: isHovered,
    };

    const styleProps: BaseButtonStyledProps = {
        state: {
            ...iconRenderState,
            isDisabled,
        },
        shouldFitContainer,
        size,
        focusMode,
        isLoading,
        appearance: btnAppearance.replace("button-", "") as ButtonAppearances | "link",
        hasOnlyIcons: children == null && (hasLeftIcon || hasRightIcon),
        hasNoIcons: !hasLeftIcon && !hasRightIcon,
        renderAs,
        themeMode,
        isCanvasEmptyAndLodestar,
    };

    const RootComponent = useMemo(() => {
        return renderAs === "a" ? BaseButtonStyled.withComponent("a") : BaseButtonStyled;
    }, [renderAs]) as StyledComponent<
        DetailedHTMLProps<HTMLAttributes<RootElementType>, RootElementType>,
        BaseButtonStyledProps,
        Record<string, unknown>
    >;

    // TODO: swap out custom component instead of merging it with our css
    return (
        <ClassNames>
            {({ cx }) => (
                <RootComponent
                    {...rest}
                    data-client-id={clientId}
                    ref={innerRef}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onMouseDown={onMouseDown as React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>}
                    onMouseUp={onMouseUp as React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    aria-disabled={isDisabled}
                    className={cx(className, FOCUS_USING_BEFORE_CLASS_NAME)}
                    onClick={onClick as React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>}
                    onDoubleClick={onDoubleClick as React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>}
                    tabIndex={tabIndex}
                    {...styleProps}
                >
                    {isLoading && (
                        <LoadingSpinner appearance={btnAppearance as ButtonAppearances} themeMode={themeMode} />
                    )}
                    {iconBefore && (
                        <IconWrapper
                            hasLeftIcon={hasLeftIcon}
                            hasRightIcon={hasRightIcon}
                            hasOnlyIcons={styleProps.hasOnlyIcons}
                            iconLocation="before"
                            isLoading={isLoading}
                            appearance={btnAppearance}
                            size={size}
                            themeMode={themeMode}
                        >
                            {typeof iconBefore === "function" ? iconBefore(iconRenderState) : iconBefore}
                        </IconWrapper>
                    )}
                    {children && (
                        <Content
                            size={size}
                            isLoading={isLoading}
                            appearance={btnAppearance}
                            hasLeftIcon={hasLeftIcon}
                            hasRightIcon={hasRightIcon}
                            themeMode={themeMode}
                            hideEmptyContent={rest.hideEmptyContent}
                        >
                            {children}
                        </Content>
                    )}
                    {iconAfter && (
                        <IconWrapper
                            hasLeftIcon={hasLeftIcon}
                            hasRightIcon={hasRightIcon}
                            iconLocation="after"
                            hasOnlyIcons={styleProps.hasOnlyIcons}
                            isLoading={isLoading}
                            appearance={btnAppearance}
                            size={size}
                            themeMode={themeMode}
                        >
                            {typeof iconAfter === "function" ? iconAfter(iconRenderState) : iconAfter}
                        </IconWrapper>
                    )}
                    {badgeProps && (
                        <ButtonBadge
                            data-client-id={getClientId(clientId, "badge")}
                            size={getBadgeSize(size)}
                            backgroundColor={
                                isDisabled || badgeProps.emphasis === "weak"
                                    ? color("color--background--notifications--weak", ursaColors.slate300)
                                    : color("color--background--notifications--default", ursaColors.orange500)
                            }
                            buttonSize={size}
                            color={color("color--text--neutral-inverse--strong", ursaColors.neutral00)}
                            {...badgeProps}
                        >
                            {!size.includes("small") &&
                                badgeProps.number !== null &&
                                (badgeProps.number && badgeProps.number > 99 ? "99+" : badgeProps.number)}
                        </ButtonBadge>
                    )}
                </RootComponent>
            )}
        </ClassNames>
    );
};
